// Escapes a single CSV field per RFC 4180 (wrap in quotes and double any
// internal quotes whenever the value contains a comma, quote, or newline),
// and neutralizes leading =, +, -, or @ characters that spreadsheet apps
// (Excel, Sheets) interpret as the start of a formula — otherwise
// user-supplied free text (sponsor name, PI name, etc.) could execute as a
// formula/DDE payload when the exported CSV is opened.
const escapeCsvField = (value: string): string => {
  const guarded = /^[=+\-@]/.test(value) ? `'${value}` : value;
  if (/[",\n\r]/.test(guarded)) {
    return `"${guarded.replace(/"/g, '""')}"`;
  }
  return guarded;
};

export const rowsToCsv = (headers: string[], rows: string[][]): string => {
  const lines = [headers, ...rows].map(row =>
    row.map(escapeCsvField).join(',')
  );
  // CRLF is the RFC 4180 line ending and what Excel expects.
  return lines.join('\r\n');
};

interface ExportableColumnDef<TData> {
  id?: string;
  accessorKey?: string;
  header?: unknown;
  accessorFn?: (row: TData, index: number) => unknown;
  meta?: {
    exportable?: boolean;
    csvFormatter?: (value: unknown, row: TData) => string;
  };
}

const columnId = <TData>(column: ExportableColumnDef<TData>): string =>
  column.id ?? column.accessorKey ?? '';

const columnValue = <TData>(
  row: TData,
  column: ExportableColumnDef<TData>
): unknown => {
  if (column.accessorFn) return column.accessorFn(row, 0);
  if (column.accessorKey)
    return (row as Record<string, unknown>)[column.accessorKey];
  return undefined;
};

/**
 * Builds and downloads a CSV from a full row set the caller already fetched
 * (e.g. an unpaginated request), using the same column defs a DataTable would
 * — so a server-paginated table's CSV export still covers every filtered
 * row, not just the currently-loaded page. Mirrors DataTable's own
 * `exportCsv` (visible + ordered columns, `exportable`/`csvFormatter` meta)
 * without needing a mounted table instance.
 */
export const exportRows = <TData>({
  rows,
  columns,
  columnOrder,
  columnVisibility,
  filename,
}: {
  rows: TData[];
  columns: ExportableColumnDef<TData>[];
  columnOrder?: string[];
  columnVisibility?: Record<string, boolean>;
  filename: string;
}): void => {
  const visibleColumns = columns.filter(
    column =>
      columnVisibility?.[columnId(column)] !== false &&
      column.meta?.exportable !== false
  );
  const orderedColumns = columnOrder
    ? [...visibleColumns].sort(
        (a, b) =>
          columnOrder.indexOf(columnId(a)) - columnOrder.indexOf(columnId(b))
      )
    : visibleColumns;

  const headers = orderedColumns.map(column =>
    typeof column.header === 'string' ? column.header : columnId(column)
  );
  const csvRows = rows.map(row =>
    orderedColumns.map(column => {
      const value = columnValue(row, column);
      return column.meta?.csvFormatter
        ? column.meta.csvFormatter(value, row)
        : String(value ?? '');
    })
  );

  downloadCsv(rowsToCsv(headers, csvRows), filename);
};

export const downloadCsv = (csv: string, filename: string): void => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
